"use client";

import React, { useState, useEffect } from "react";
import { Zap, CircleCheck, Database, PhoneIncoming } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function LeadAcceleration() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const [progress, setProgress] = useState(0);
  const [activeStep, setActiveStep] = useState(0);
  useEffect(() => {
    if (!isVisible) return;
    const duration = 2000;
    const intervalTime = 20;
    const steps = duration / intervalTime;
    const increment = 100 / steps;
    const timer = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          setTimeout(() => {
            setProgress(0);
            setActiveStep(0);
          }, 1000);
          return 100;
        }
        return prev + increment;
      });
    }, intervalTime);
    return () => clearInterval(timer);
  }, [isVisible]);
  useEffect(() => {
    if (progress < 33) setActiveStep(0);else if (progress < 66) setActiveStep(1);else setActiveStep(2);
  }, [progress]);
  const steps = [{
    label: "Call Received",
    icon: PhoneIncoming
  }, {
    label: "AI Qualifying",
    icon: Zap
  }, {
    label: "CRM Updated",
    icon: Database
  }];
  return <section id="lead-acceleration" ref={ref} className="relative w-full py-24 overflow-hidden bg-slate-950 text-white">
      {}
      <div className="absolute inset-0 pointer-events-none select-none overflow-hidden">
        {}
        <div className="absolute top-1/2 left-[-20%] w-[140%] h-[400px] -translate-y-1/2 -rotate-12 bg-[conic-gradient(from_90deg_at_50%_50%,transparent_0%,var(--color-primary)_50%,transparent_100%)] opacity-20 blur-[80px] mix-blend-screen animate-pulse" />
        
        {}
        <div className="absolute top-1/3 right-[-10%] w-[80%] h-[200px] -rotate-45 bg-blue-500/20 blur-[60px] mix-blend-screen" />
        
        {}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)]" />
      </div>

      <div className="relative z-10 container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          
          {}
          <div className={`space-y-8 transition-all duration-700 ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-10"}`}>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-900/30 border border-blue-500/30 text-blue-300 text-sm font-medium">
              <Zap className="w-4 h-4 fill-blue-300" />
              <span>Speed of Light Automation</span>
            </div>
            
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white">
              Instant Lead <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-white">
                Qualification
              </span>
            </h2>
            
            <p className="text-lg text-slate-300 max-w-xl leading-relaxed">
              Don't let hot leads go cold. Our AI engages, qualifies, and syncs data to your CRM in milliseconds—turning interest into action before the phone even hangs up.
            </p>

            <div className="flex flex-wrap gap-4 pt-4">
              <Button asChild size="lg" className="bg-blue-600 hover:bg-blue-500 text-white border-none shadow-[0_0_20px_rgba(37,99,235,0.3)]">
                <Link to="/SolutionsPage">
                  Accelerate Your Sales
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-slate-700 bg-transparent text-white hover:bg-slate-800 hover:text-white">
                <Link to="/TechnologyPage">
                  View Architecture
                </Link>
              </Button>
            </div>
          </div>

          {}
          <div className={`relative transition-all duration-700 delay-200 ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-10"}`}>
            {}
            <div className="relative rounded-2xl border border-slate-700/50 bg-slate-900/60 backdrop-blur-xl p-8 shadow-2xl overflow-hidden">
              {}
              <div className="flex items-center justify-between mb-8 border-b border-slate-700/50 pb-4">
                <div className="flex items-center gap-3">
                  <div className="h-3 w-3 rounded-full bg-red-500" />
                  <div className="h-3 w-3 rounded-full bg-yellow-500" />
                  <div className="h-3 w-3 rounded-full bg-green-500" />
                </div>
                <span className="text-xs font-mono text-slate-400">LIVE_PROCESS_VIEWER_v2.0</span>
              </div>

              {}
              <div className="space-y-8">
                <div className="flex justify-between items-end mb-2">
                  <span className="text-2xl font-mono font-bold text-blue-400">
                    {Math.min(Math.round(progress), 100)}%
                  </span>
                  <span className="text-xs text-slate-400 uppercase tracking-widest">
                    Processing Time: &lt; 2.0s
                  </span>
                </div>

                <Progress value={progress} className="h-3 bg-slate-800" />

                {}
                <div className="grid grid-cols-3 gap-4 mt-8">
                  {steps.map((step, i) => {
                  const isActive = i === activeStep;
                  const isCompleted = i < activeStep || progress === 100 && i === 2;
                  return <div key={i} data-index={i} className={`flex flex-col items-center text-center gap-3 transition-all duration-300 ${isActive || isCompleted ? "opacity-100" : "opacity-40"}`}>
                        <div className={`
                          relative flex items-center justify-center w-12 h-12 rounded-full border-2 transition-all duration-300
                          ${isActive ? "border-blue-500 bg-blue-500/20 scale-110 shadow-[0_0_15px_rgba(59,130,246,0.5)]" : ""}
                          ${isCompleted ? "border-green-500 bg-green-500/20 text-green-400" : "border-slate-700 bg-slate-800 text-slate-400"}
                          ${!isActive && !isCompleted ? "text-slate-500" : "text-blue-400"}
                        `}>
                          {isCompleted ? <CircleCheck className="w-6 h-6" /> : <step.icon className="w-6 h-6" />}
                        </div>
                        <span className={`text-sm font-medium ${isActive ? "text-white" : "text-slate-400"}`}>
                          {step.label}
                        </span>
                      </div>;
                })}
                </div>
              </div>

              {}
              <div className="mt-8 pt-6 border-t border-slate-700/50">
                <div className="font-mono text-[10px] leading-relaxed text-slate-500 opacity-70">
                  <p>{`> INCOMING_CALL_DETECTED: ID_99281`}</p>
                  <p className={progress > 33 ? "text-blue-400" : ""}>{`> ANALYZING_INTENT... ${progress > 33 ? "DONE" : "WAITING"}`}</p>
                  <p className={progress > 66 ? "text-blue-400" : ""}>{`> SYNC_TO_CRM... ${progress > 66 ? "SUCCESS" : "WAITING"}`}</p>
                </div>
              </div>
            </div>

            {}
            <div className="absolute -inset-4 bg-blue-600/20 blur-3xl -z-10 rounded-full opacity-50" />
          </div>
        </div>
      </div>
    </section>;
}